import { useMemo } from "react";
import type { Column } from "@legal-500/legal500-design-system";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import type {
  ApplyRowResult,
  ApplyRowStatus,
} from "../../../api/officeIdentityBackfill.service.js";
import styles from "./OfficeIdentityBackfill.module.css";

/**
 * The per-row outcome table shown after a backfill or restore run (DEV-2803).
 * Shared by both flows: the restore flow has no office names to show, so the
 * Office column only appears when the caller can resolve one.
 */

export const STATUS_LABELS: Record<ApplyRowStatus, string> = {
  applied: "Applied",
  noop: "No-op",
  skipped_not_empty: "Skipped — not empty",
  invalid: "Invalid",
  error: "Error",
};

const PILL_CLASSES: Record<ApplyRowStatus, string> = {
  applied: styles.pillOk as string,
  noop: styles.pillNeutral as string,
  // Warning, not error: the field already held a value, which is the endpoint
  // refusing to overwrite rather than something having gone wrong.
  skipped_not_empty: styles.pillWarning as string,
  invalid: styles.pillError as string,
  error: styles.pillError as string,
};

/** Tallies results by status, for the summary tiles. */
export function countByStatus(results: ApplyRowResult[]): (status: ApplyRowStatus) => number {
  const tally = new Map<ApplyRowStatus, number>();
  for (const result of results) {
    tally.set(result.status, (tally.get(result.status) ?? 0) + 1);
  }
  return status => tally.get(status) ?? 0;
}

type ResultsTableProps = {
  results: ApplyRowResult[];
  /** Resolves an office name for a result. Omitted by the restore flow. */
  nameFor?: (result: ApplyRowResult) => string | null;
};

type TableRow = {
  id: string;
  name: string | null;
  officeId: string;
  field: string;
  previous: string | null;
  status: ApplyRowStatus;
  message: string | null;
};

export function ResultsTable({ results, nameFor }: ResultsTableProps) {
  const rows = useMemo<TableRow[]>(
    () =>
      results.map((result, index) => ({
        // Index-prefixed: a malformed upload could repeat officeId+field.
        id: `${index}-${result.officeId}-${result.field}`,
        name: nameFor ? nameFor(result) : null,
        officeId: result.officeId,
        field: result.field,
        previous: result.previous,
        status: result.status,
        message: result.message ?? null,
      })),
    [results, nameFor],
  );

  const columns = useMemo<Column<TableRow>[]>(() => {
    const nameColumn: Column<TableRow>[] = nameFor
      ? [
          {
            header: "Office",
            accessor: "name",
            wrap: true,
            render: (_value, row) => <span className={styles.name}>{row.name || "—"}</span>,
          },
        ]
      : [];
    return [
      ...nameColumn,
      {
        header: "Office id",
        accessor: "officeId",
        // The first eight characters are enough to tell rows apart by eye; the
        // full id stays a hover away rather than widening every row.
        render: (_value, row) => (
          <span className={styles.mono} title={row.officeId}>
            {row.officeId.slice(0, 8)}
          </span>
        ),
      },
      {
        header: "Field",
        accessor: "field",
        render: (_value, row) => <span className={styles.mono}>{row.field}</span>,
      },
      {
        header: "Previous",
        accessor: "previous",
        wrap: true,
        render: (_value, row) => <span className={styles.mono}>{row.previous ?? "—"}</span>,
      },
      {
        header: "Status",
        accessor: "status",
        render: (_value, row) => (
          <span
            className={`${styles.pill} ${PILL_CLASSES[row.status]}`}
            title={row.message ?? undefined}
          >
            {STATUS_LABELS[row.status]}
          </span>
        ),
      },
      {
        header: "Message",
        accessor: "message",
        wrap: true,
        render: (_value, row) => <span className={styles.rowNote}>{row.message ?? "—"}</span>,
      },
    ];
  }, [nameFor]);

  return <DataTable data={rows} columns={columns} emptyMessage="No results." />;
}
